// HistoryChart — the full time-series view, drawn to the v2 chart rules
// (cloud/docs/web-refresh-v2.md "Charts", TOM-194): series told apart by
// stroke (solid chart line, dashed ink, dotted grey), a 0.5px grid, a min/max
// envelope and area fill under a single series only, window extremes
// direct-labeled with ringed dots, faint alternating day bands on multi-day
// windows, the accent "now" circle, labelled threshold lines, and a crosshair
// tooltip (pointer + keyboard) that reads every series at once.

import { useCallback, useId, useMemo, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';

// The tick and band helpers are the page library's (TOM-665): a page's ticks
// and day bands are this chart's, from one copy.
import {
  pageDayStarts as dayStarts,
  pageLinearTicks as linearTicks,
  pageSpacedTicks as spacedTicks,
  pageThinLabels as thinLabels,
  pageTimeTickInstants as timeTicks,
} from '@neomaris/controls/core';

import { clamp } from './lib/arc';
import { areaPathFor, bandPathFor, linePathFor, type CurveKind } from './lib/curve';
import { placeExtremeLabel, type Box, type Circle } from './lib/extremeLabels';
import {
  estimateTextWidth,
  formatClock,
  formatPointTime,
  formatTickTime,
  formatValue,
  formatDayLabel,
  thresholdLabel,
} from './lib/format';
import { linearScale, padDomain } from './lib/scales';
import {
  findExtremes,
  localExtremes,
  nearestIndex,
  pastThreshold,
  segments,
  seriesExtent,
  timeExtent,
  valueAt,
  type SeriesPoint,
} from './lib/series';
import { useMeasure } from './lib/useMeasure';
import {
  areaColor,
  seriesStroke,
  seriesTone,
  toneColor,
  type ChartThreshold,
  type Tone,
} from './types';

export interface HistorySeries {
  id: string;
  label?: string;
  points: SeriesPoint[];
  /** The series style. Default: series 1, 2, 3 by position (`seriesTone`).
      `forecast` draws Neomaris's forecast: dashed grey, a dash no series uses. */
  tone?: Tone;
  /** Soft wash under this trace. Drawn only on a single-series chart. */
  area?: boolean;
  /** Line style: smooth monotone (default), straight, or step-after. */
  curve?: CurveKind;
  /** Display unit for this series' tooltip rows (falls back to the chart
      `unit`). Series of different DIMENSIONS don't belong on one scale —
      give them separate charts/panels. */
  unit?: string;
}

/** A quantity drawn as thin columns in a strip along the bottom, on its own
    zero-based scale — rain probability under temperature, gusts under wind.
    An honest composite: two panels sharing the time axis, never two value
    scales on one plot. */
export interface HistoryBars {
  id: string;
  label?: string;
  points: SeriesPoint[];
  tone?: Tone;
  unit?: string;
}

/** A labeled moment on the time axis: a dashed vertical rule with its label
    above the plot — a prompt-version change on an eval trend, say. Not data:
    it marks when something about the series changed. */
export interface HistoryMarker {
  t: number;
  label: string;
}

export interface HistoryChartProps {
  series: HistorySeries[];
  /** Bottom-strip columns sharing the time axis (own zero-based scale). */
  bars?: HistoryBars;
  /** Plot height in px (the component adds legend height itself). */
  height?: number;
  /** Grow the plot to fill the container's height (measured) instead of the
      fixed `height` prop — lets the chart fill a flex/grid cell exactly, the
      same contract as Sparkline's `fillHeight`. Give the plot a CSS height
      where the parent does not size it (scrolling layouts); that height acts
      as the flex basis. */
  fillHeight?: boolean;
  /** 'compact' drops the x-tick row and slims the gutters — for half-height
      cells where `extremeTimes` labels already say when. */
  frame?: 'regular' | 'compact';
  unit?: string;
  valueFormat?: (v: number) => string;
  /** 'auto' pads the data extent; 'zero' also pins the baseline at 0. */
  domain?: [number, number] | 'auto' | 'zero';
  /** min/max envelope behind the averaged trace (needs lo/hi on points).
      Drawn only on a single-series chart. */
  showBand?: boolean;
  /** Limits drawn as labelled dashed lines. The automatic domain stretches to
      show them, and a latest value of the first series past one is named in
      the line's label ("· now below"). */
  thresholds?: ChartThreshold[];
  /** 'window': label the window min/max. 'local': label every meaningful
      peak and trough (tide highs/lows). true = 'window'.
      Default: 'window' for a single series, off for several. */
  markExtremes?: boolean | 'window' | 'local';
  /** Small clock time beside each 'local' extreme label. */
  extremeTimes?: boolean;
  /** The "now" marker on a forecast or tide: a hairline cursor, and the accent
      circle on the first series at that time. Without it the circle sits on
      the first series' latest point. */
  nowMs?: number;
  /** Labeled moments on the time axis (dashed rules). Markers outside the
      time window are not drawn; a label that would overlap the one before it
      is dropped and stays on the rule's tooltip. */
  markers?: HistoryMarker[];
  /** X domain override; defaults to the data extent. */
  timeWindow?: [number, number];
  /** Faint alternating day shading. Default: windows of 40h and longer. */
  dayBands?: boolean;
  interactive?: boolean;
  /** Force the legend on/off. Default: shown for ≥2 series or bars — set
      true on single-series panels whose card title doesn't name the series. */
  legend?: boolean;
  /** Refetch-in-flight: hold the previous render, slightly dimmed. */
  dimmed?: boolean;
  emptyText?: string;
  ariaLabel?: string;
  className?: string;
}

interface Hover {
  t: number;
  source: 'pointer' | 'keys';
}

const MARGIN = { top: 18, right: 12, bottom: 22, left: 42 };

/** The least distance between two y-axis labels' baselines: a 10.5px label and air. */
const Y_TICK_MIN_GAP_PX = 16;
/** The compact frame: no x-tick row and slim gutters — for half-height panels
    whose extreme labels already carry their clock times (the tide). */
const COMPACT_MARGIN = { top: 14, right: 8, bottom: 6, left: 30 };
/** A plot at least this wide takes the wide stroke and marker sizes. */
const WIDE_PLOT_PX = 640;

export function HistoryChart({
  series,
  bars,
  height = 240,
  fillHeight = false,
  frame = 'regular',
  unit,
  valueFormat = (v) => formatValue(v),
  domain = 'auto',
  showBand = true,
  thresholds,
  markExtremes,
  extremeTimes = false,
  nowMs,
  markers,
  timeWindow,
  dayBands,
  interactive = true,
  legend,
  dimmed = false,
  emptyText = 'no data yet',
  ariaLabel,
  className,
}: HistoryChartProps) {
  const [ref, size] = useMeasure<HTMLDivElement>();
  const gradBase = useId();
  const [hover, setHover] = useState<Hover | null>(null);

  const width = size.width;
  const pointLists = useMemo(() => {
    const lists = series.map((s) => s.points);
    return bars !== undefined ? [...lists, bars.points] : lists;
  }, [series, bars]);
  const tExt = useMemo(() => {
    if (timeWindow !== undefined) return { min: timeWindow[0], max: timeWindow[1] };
    return timeExtent(pointLists);
  }, [pointLists, timeWindow]);
  const lineLists = useMemo(() => series.map((s) => s.points), [series]);
  // The envelope and the wash belong to a single series: under several they
  // pile into a grey smear that no single line owns (v2 chart rules).
  const single = series.length === 1;
  const band = showBand && single;
  const vExt = useMemo(() => {
    const ext = seriesExtent(lineLists, band);
    if (ext === null || thresholds === undefined || thresholds.length === 0) return ext;
    // A threshold the domain leaves out is a label nobody sees.
    const values = thresholds.map((th) => th.value).filter(Number.isFinite);
    return { min: Math.min(ext.min, ...values), max: Math.max(ext.max, ...values) };
  }, [lineLists, band, thresholds]);

  // Measured when filling (the parent sizes the plot), the prop otherwise.
  const chartH = fillHeight ? size.height : height;
  const M = frame === 'compact' ? COMPACT_MARGIN : MARGIN;
  const ready = width > 0 && chartH > 0 && tExt !== null && vExt !== null && tExt.max > tExt.min;
  const tMin = tExt?.min ?? 0;
  const tMax = tExt?.max ?? 1;
  const spanMs = ready ? tMax - tMin : 0;
  const plotW = Math.max(0, width - M.left - M.right);
  const plotH = Math.max(0, chartH - M.top - M.bottom);
  // iOS draws a wide layout's lines half a pixel heavier and its "now" marker
  // larger (3.5 px and 16 px, against 3 px and 12 px).
  const wide = plotW >= WIDE_PLOT_PX;
  const baseStroke = wide ? 3.5 : 3;
  const stripH = bars !== undefined ? Math.min(64, Math.max(28, Math.round(plotH * 0.24))) : 0;
  const stripGap = bars !== undefined ? 8 : 0;
  const lineBottom = M.top + plotH - stripH - stripGap;
  const stripTop = M.top + plotH - stripH;

  const extremesMode: 'none' | 'window' | 'local' =
    markExtremes === 'local'
      ? 'local'
      : markExtremes === 'window' || markExtremes === true
        ? 'window'
        : markExtremes === false
          ? 'none'
          : series.length === 1
            ? 'window'
            : 'none';

  // Extreme clock times need two label rows of plot; below that they pile
  // onto the value labels and the curve (the 1024-wide floor). Value labels
  // stay — the time is the first thing to yield.
  const showExtremeTimes = extremeTimes && plotH >= 36;

  const [d0, d1] = useMemo<[number, number]>(() => {
    if (Array.isArray(domain)) return domain;
    if (vExt === null) return [0, 1];
    // Local extreme labels (and their times) live inside the plot — give
    // the peaks and troughs the headroom they need.
    const pad = extremesMode === 'local' ? (showExtremeTimes ? 0.17 : 0.12) : 0.09;
    return padDomain(vExt.min, vExt.max, pad, { zero: domain === 'zero' });
  }, [domain, vExt, extremesMode, showExtremeTimes]);

  const x = useMemo(
    () => linearScale(tMin, tMax, M.left, M.left + plotW),
    [tMin, tMax, plotW, M.left]
  );
  const y = useMemo(() => linearScale(d0, d1, lineBottom, M.top), [d0, d1, lineBottom, M.top]);

  // Reference series for snapping: the first one with data.
  const refPoints = useMemo(() => pointLists.find((p) => p.length > 0) ?? [], [pointLists]);

  const snapTo = useCallback(
    (t: number): number | null => {
      const i = nearestIndex(refPoints, t);
      return i === -1 ? null : refPoints[i]!.t;
    },
    [refPoints]
  );

  const onPointer = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!interactive || !ready) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const t = tMin + ((px - M.left) / Math.max(1, plotW)) * spanMs;
      const snapped = snapTo(clamp(t, tMin, tMax));
      if (snapped !== null) setHover({ t: snapped, source: 'pointer' });
    },
    [interactive, ready, tMin, tMax, plotW, spanMs, snapTo, M.left]
  );

  const onKey = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!interactive || !ready || refPoints.length === 0) return;
      const current = hover === null ? refPoints.length - 1 : nearestIndex(refPoints, hover.t);
      let next: number | null = null;
      if (e.key === 'ArrowLeft') next = Math.max(0, current - 1);
      else if (e.key === 'ArrowRight') next = Math.min(refPoints.length - 1, current + 1);
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = refPoints.length - 1;
      else if (e.key === 'Escape') {
        setHover(null);
        return;
      } else return;
      e.preventDefault();
      setHover({ t: refPoints[next]!.t, source: 'keys' });
    },
    [interactive, ready, refPoints, hover]
  );

  const wantDayBands = ready && (dayBands ?? spanMs >= 40 * 3_600_000);
  const midnights = ready && wantDayBands ? dayStarts(tMin - 86_400_000, tMax) : [];
  const showTopDayLabels = wantDayBands && spanMs <= 48 * 3_600_000;

  // Labels 16px apart at least: a short plot shows fewer numbers rather than
  // numbers printed into each other (TOM-445).
  const yTicks = ready
    ? spacedTicks(linearTicks(d0, d1, 4), (lineBottom - M.top) / (d1 - d0), Y_TICK_MIN_GAP_PX)
    : [];
  const xTickTimes = ready
    ? thinLabels(
        timeTicks(
          tMin,
          tMax,
          // Past two days the labels are day names, so ticks must be ≥ 1/day.
          spanMs > 48 * 3_600_000
            ? Math.min(Math.max(2, Math.floor(plotW / 76)), Math.round(spanMs / 86_400_000))
            : Math.max(2, Math.floor(plotW / 76))
        ),
        (t) => formatTickTime(t, spanMs),
        x,
        plotW
      )
    : [];

  const hoverX = hover === null || !ready ? null : x(hover.t);
  const flipTooltip = hoverX !== null && hoverX > M.left + plotW * 0.58;

  // The accent "now" circle rides the first series: at its latest point, or at
  // `nowMs` on a forecast or a tide. A measured trace that hands over to a
  // forecast at now has the circle on whichever series is drawn there.
  const firstPoints = series[0]?.points ?? [];
  const nowInWindow = nowMs !== undefined && nowMs >= tMin && nowMs <= tMax;
  let nowPoint: { t: number; v: number } | null = null;
  if (ready && nowMs === undefined) nowPoint = lastPoint(firstPoints);
  else if (ready && nowInWindow) {
    for (const s of series) {
      const v = valueAt(s.points, nowMs);
      if (v !== null) {
        nowPoint = { t: nowMs, v };
        break;
      }
    }
  }
  // Where a high or low label may sit: across the plot, from the svg's top to
  // the line's floor over a bars strip, else to the plot's foot, or the svg's
  // in the compact frame, which draws no time axis there (TOM-445).
  const labelBounds: Box = {
    x0: M.left,
    x1: M.left + plotW,
    y0: 0,
    y1: stripH > 0 ? lineBottom : frame === 'compact' ? chartH : M.top + plotH,
  };
  const latestValue = lastPoint(firstPoints)?.v ?? null;
  const unitSuffix = unit !== undefined && unit !== '' ? ` ${unit}` : '';

  // One stable, keyed tree for every state: the measured plot node must
  // never be replaced by reconciliation (a conditional sibling shifting
  // its index once detached it, and the ResizeObserver then reported the
  // dead node as 0×0 — un-readying the chart forever).
  return (
    <div className={cx('chart-history', fillHeight ? 'chart-history--fill' : undefined, className)}>
      {(legend ?? (series.length > 1 || bars !== undefined)) && (
        <div key="legend" className="chart-legend">
          {series.map((s, i) => (
            <span key={s.id} className="chart-legend-item">
              <LineKey tone={s.tone ?? seriesTone(i)} />
              {s.label ?? s.id}
            </span>
          ))}
          {bars !== undefined && (
            <span className="chart-legend-item">
              <span
                className="chart-legend-key chart-legend-key--bar"
                style={{ '--chart-tone': toneColor(bars.tone ?? BAR_STRIP_TONE) } as CSSProperties}
              />
              {bars.label ?? bars.id}
            </span>
          )}
        </div>
      )}
      <div
        key="plot"
        className="chart-history-plot"
        ref={ref}
        style={{ height: fillHeight ? undefined : height, opacity: dimmed ? 0.55 : undefined }}
        role="img"
        aria-label={ariaLabel ?? 'history chart'}
        tabIndex={interactive ? 0 : undefined}
        onPointerMove={onPointer}
        onPointerDown={onPointer}
        onPointerLeave={() => setHover((h) => (h?.source === 'pointer' ? null : h))}
        onKeyDown={onKey}
        onBlur={() => setHover((h) => (h?.source === 'keys' ? null : h))}
      >
        {!ready && <div className="chart-empty">{emptyText}</div>}
        {ready && (
          <svg className="chart-svg" width={width} height={chartH}>
            {/* day bands: shade alternate local days */}
            {wantDayBands &&
              midnights.map((m, i) => {
                if (i % 2 === 0) return null;
                const x0 = clamp(x(m), M.left, M.left + plotW);
                const x1 = clamp(x(midnights[i + 1] ?? tMax), M.left, M.left + plotW);
                if (x1 - x0 < 1) return null;
                return (
                  <rect
                    key={`band-${m}`}
                    x={x0}
                    y={M.top}
                    width={x1 - x0}
                    height={plotH}
                    fill="var(--chart-band)"
                  />
                );
              })}
            {showTopDayLabels &&
              midnights.map((m, i) => {
                const x0 = clamp(x(m), M.left, M.left + plotW);
                const x1 = clamp(x(midnights[i + 1] ?? tMax), M.left, M.left + plotW);
                if (x1 - x0 < 56) return null;
                return (
                  <text
                    key={`day-${m}`}
                    className="chart-day-label"
                    x={(x0 + x1) / 2}
                    y={M.top + 12}
                    textAnchor="middle"
                  >
                    {formatDayLabel(m + 43_200_000)}
                  </text>
                );
              })}

            {/* horizontal grid + y tick labels */}
            {yTicks.map((v) => (
              <g key={`y-${v}`}>
                <line
                  x1={M.left}
                  x2={M.left + plotW}
                  y1={y(v)}
                  y2={y(v)}
                  stroke="var(--chart-grid)"
                  strokeWidth={0.5}
                />
                <text className="chart-tick-label" x={M.left - 8} y={y(v) + 3.5} textAnchor="end">
                  {valueFormat(v)}
                </text>
              </g>
            ))}

            {/* thresholds: a labelled dashed line, under the data */}
            {thresholds?.map((th) => {
              const ty = y(th.value);
              if (!Number.isFinite(ty) || ty < M.top - 0.5 || ty > lineBottom + 0.5) return null;
              const past = pastThreshold(latestValue, th);
              return (
                <g key={`th-${th.label}-${th.value}`} className="chart-threshold">
                  <line
                    x1={M.left}
                    x2={M.left + plotW}
                    y1={ty}
                    y2={ty}
                    stroke="var(--chart-ink-2)"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                  />
                  <text
                    className={
                      past
                        ? 'chart-threshold-label chart-threshold-label--past'
                        : 'chart-threshold-label'
                    }
                    x={M.left + 4}
                    y={ty - 5}
                    textAnchor="start"
                  >
                    {thresholdLabel(
                      th.label,
                      `${valueFormat(th.value)}${unitSuffix}`,
                      past,
                      th.direction
                    )}
                  </text>
                </g>
              );
            })}

            {/* baseline + x ticks */}
            <line
              x1={M.left}
              x2={M.left + plotW}
              y1={M.top + plotH}
              y2={M.top + plotH}
              stroke="var(--chart-axis)"
              strokeWidth={1}
            />
            {frame !== 'compact' &&
              xTickTimes.map(({ t, label }) => (
                <text
                  key={`x-${t}`}
                  className="chart-tick-label"
                  x={x(t)}
                  y={M.top + plotH + 15}
                  textAnchor="middle"
                >
                  {label}
                </text>
              ))}

            {/* series marks */}
            {series.map((s, si) => {
              const stroke = seriesStroke(s.tone ?? seriesTone(si), baseStroke, wide);
              const color = stroke.color;
              const fill = areaColor(s.tone ?? seriesTone(si));
              const curve = s.curve ?? 'smooth';
              const segs = segments(s.points);
              const gradId = `${gradBase}-${si}`;
              const wantArea = single && (s.area ?? !showBandHasData(s.points, band));
              return (
                <g key={s.id}>
                  {wantArea && (
                    <defs>
                      <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="0%"
                          style={{ stopColor: fill, stopOpacity: 'var(--chart-area-alpha)' }}
                        />
                        <stop
                          offset="100%"
                          style={{ stopColor: fill, stopOpacity: 'var(--chart-area-floor, 0.02)' }}
                        />
                      </linearGradient>
                    </defs>
                  )}
                  {segs.map((seg, gi) => {
                    const pts = seg.map((p) => ({ x: x(p.t), y: y(p.v) }));
                    const bandPts = band
                      ? seg.filter((p) => p.lo !== undefined && p.hi !== undefined)
                      : [];
                    return (
                      <g key={gi}>
                        {bandPts.length > 1 && (
                          <path
                            d={bandPathFor(
                              bandPts.map((p) => ({ x: x(p.t), y: y(p.hi!) })),
                              bandPts.map((p) => ({ x: x(p.t), y: y(p.lo!) })),
                              curve
                            )}
                            fill="var(--chart-track)"
                          />
                        )}
                        {wantArea && pts.length > 1 && (
                          <path d={areaPathFor(pts, lineBottom, curve)} fill={`url(#${gradId})`} />
                        )}
                        <path
                          d={linePathFor(pts, curve)}
                          fill="none"
                          stroke={color}
                          strokeWidth={stroke.width}
                          strokeDasharray={stroke.dash}
                          strokeLinecap={stroke.cap}
                          strokeLinejoin="round"
                        />
                        {pts.length === 1 && (
                          <circle cx={pts[0]!.x} cy={pts[0]!.y} r={stroke.width} fill={color} />
                        )}
                      </g>
                    );
                  })}
                  {extremesMode !== 'none' && (
                    <ExtremeLabels
                      mode={extremesMode}
                      points={s.points}
                      x={x}
                      y={y}
                      color={color}
                      valueFormat={valueFormat}
                      showTimes={showExtremeTimes}
                      bounds={labelBounds}
                      curveY={(px) => {
                        const v = valueAt(s.points, tMin + ((px - M.left) / plotW) * spanMs);
                        return v === null ? null : y(v);
                      }}
                      now={
                        nowPoint === null
                          ? null
                          : { cx: x(nowPoint.t), cy: y(nowPoint.v), r: (wide ? 9 : 7) + 1 }
                      }
                      nowLine={
                        nowMs !== undefined && nowInWindow
                          ? { x0: x(nowMs) - 1, x1: x(nowMs) + 1, y0: M.top, y1: lineBottom }
                          : null
                      }
                    />
                  )}
                </g>
              );
            })}

            {/* bottom bars strip (own zero-based scale, shared time axis) */}
            {bars !== undefined && (
              <BarsStrip
                bars={bars}
                x={x}
                stripTop={stripTop}
                stripBottom={M.top + plotH}
                plotLeft={M.left}
                plotW={plotW}
              />
            )}

            {/* now cursor: a hairline, so the accent circle below is the mark */}
            {nowMs !== undefined && nowInWindow && (
              <g>
                <line
                  x1={x(nowMs)}
                  x2={x(nowMs)}
                  y1={M.top}
                  y2={M.top + plotH}
                  stroke="var(--chart-axis)"
                  strokeWidth={1}
                />
                {/* No "now" text in the compact frame: the cursor line says it,
                    and an extreme labelled at the same x (high water right now
                    — common at anchor) would collide with the word. */}
                {frame !== 'compact' && (
                  <text className="chart-now-label" x={x(nowMs)} y={M.top - 5} textAnchor="middle">
                    now
                  </text>
                )}
              </g>
            )}

            {/* The "now" marker: an accent circle, 12px (16px wide), ringed in
                the card colour so it reads over the line it sits on. */}
            {nowPoint !== null && (
              <circle
                className="chart-now-marker"
                cx={x(nowPoint.t)}
                cy={y(nowPoint.v)}
                r={wide ? 9 : 7}
                fill="var(--chart-now)"
                stroke="var(--chart-surface)"
                strokeWidth={2}
              />
            )}

            {/* labeled moments on the time axis */}
            {markers !== undefined &&
              placeMarkerLabels(
                markers.filter((m) => m.t >= tMin && m.t <= tMax),
                x,
                M.left + plotW
              ).map((m) => (
                <g key={`marker-${m.t}`} className="chart-marker">
                  <title>{m.label}</title>
                  <line
                    x1={x(m.t)}
                    x2={x(m.t)}
                    y1={M.top}
                    y2={M.top + plotH}
                    stroke="var(--chart-axis)"
                    strokeWidth={1}
                    strokeDasharray="3 3"
                  />
                  {m.showLabel && (
                    <text
                      className="chart-tick-label"
                      x={x(m.t)}
                      y={M.top - 5}
                      textAnchor={m.anchor}
                    >
                      {m.label}
                    </text>
                  )}
                </g>
              ))}

            {/* crosshair */}
            {hoverX !== null && (
              <g>
                <line
                  x1={hoverX}
                  x2={hoverX}
                  y1={M.top}
                  y2={M.top + plotH}
                  stroke="var(--chart-axis)"
                  strokeWidth={1}
                />
                {series.map((s, si) => {
                  const p = pointAt(s.points, hover!.t, spanMs);
                  if (p === null) return null;
                  return (
                    <circle
                      key={s.id}
                      cx={x(p.t)}
                      cy={y(p.v)}
                      r={4.5}
                      fill={toneColor(s.tone ?? seriesTone(si))}
                      stroke="var(--chart-surface)"
                      strokeWidth={2}
                    />
                  );
                })}
              </g>
            )}
          </svg>
        )}

        {hover !== null && hoverX !== null && (
          <div
            className="chart-tooltip"
            style={{
              top: 6,
              left: flipTooltip ? hoverX - 12 : hoverX + 12,
              transform: flipTooltip ? 'translateX(-100%)' : undefined,
            }}
          >
            <div className="chart-tooltip-time">{formatPointTime(hover.t, spanMs)}</div>
            {(bars !== undefined
              ? [
                  ...series.map((s, si) => ({
                    id: s.id,
                    label: s.label ?? s.id,
                    points: s.points,
                    tone: s.tone ?? seriesTone(si),
                    unit: s.unit ?? unit,
                    bar: false,
                  })),
                  {
                    id: bars.id,
                    label: bars.label ?? bars.id,
                    points: bars.points,
                    tone: bars.tone ?? BAR_STRIP_TONE,
                    unit: bars.unit,
                    bar: true,
                  },
                ]
              : series.map((s, si) => ({
                  id: s.id,
                  label: s.label ?? s.id,
                  points: s.points,
                  tone: s.tone ?? seriesTone(si),
                  unit: s.unit ?? unit,
                  bar: false,
                }))
            ).map((row) => {
              const p = pointAt(row.points, hover.t, spanMs);
              return (
                <div key={row.id} className="chart-tooltip-row">
                  {row.bar ? (
                    <span
                      className="chart-tooltip-key chart-legend-key--bar"
                      style={{ '--chart-tone': toneColor(row.tone) } as CSSProperties}
                    />
                  ) : (
                    <LineKey tone={row.tone} />
                  )}
                  <span className="chart-tooltip-name">{row.label}</span>
                  <span className="chart-tooltip-value">
                    {p === null ? '—' : valueFormat(p.v)}
                    {p !== null && row.unit !== undefined && row.unit !== '' && (
                      <span className="chart-tooltip-unit">{row.unit}</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- helpers

/** The bottom strip is context under the lines, so it draws in the quiet grey. */
const BAR_STRIP_TONE: Tone = 'neutral';

/** A legend or tooltip key that mirrors the line it names: the same colour,
    dash and caps, so a series is found by its stroke as well as its colour. */
function LineKey({ tone }: { tone: Tone }) {
  const stroke = seriesStroke(tone, 2.5);
  return (
    <svg className="chart-legend-line" width={22} height={6} aria-hidden="true">
      <line
        x1={stroke.cap === 'round' ? 2 : 0}
        x2={stroke.cap === 'round' ? 20 : 22}
        y1={3}
        y2={3}
        stroke={stroke.color}
        strokeWidth={stroke.width}
        strokeDasharray={stroke.dash}
        strokeLinecap={stroke.cap}
      />
    </svg>
  );
}

function cx(...parts: Array<string | undefined>): string {
  return parts.filter((p) => p !== undefined && p !== '').join(' ');
}

function lastPoint(points: SeriesPoint[]): (SeriesPoint & { v: number }) | null {
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const p = points[i]!;
    if (p.v !== null && Number.isFinite(p.v)) return p as SeriesPoint & { v: number };
  }
  return null;
}

/** Sample nearest the crosshair time, within a sensible tolerance. */
function pointAt(
  points: SeriesPoint[],
  t: number,
  spanMs: number
): (SeriesPoint & { v: number }) | null {
  const i = nearestIndex(points, t);
  if (i === -1) return null;
  const p = points[i]!;
  if (p.v === null || !Number.isFinite(p.v)) return null;
  if (Math.abs(p.t - t) > Math.max(spanMs / 20, 60_000)) return null;
  return p as SeriesPoint & { v: number };
}

function showBandHasData(points: SeriesPoint[], showBand: boolean): boolean {
  if (!showBand) return false;
  return points.some((p) => p.lo !== undefined && p.hi !== undefined && p.hi !== p.lo);
}

/** Marker labels left to right, each dropped when it would overlap the last one drawn. */
function placeMarkerLabels(
  markers: HistoryMarker[],
  x: (t: number) => number,
  plotRight: number
): Array<HistoryMarker & { showLabel: boolean; anchor: 'start' | 'middle' | 'end' }> {
  let lastRight = -Infinity;
  return [...markers]
    .sort((a, b) => a.t - b.t)
    .map((m) => {
      const w = estimateTextWidth(m.label, 10.5);
      const px = x(m.t);
      const anchor: 'start' | 'middle' | 'end' = px + w / 2 > plotRight ? 'end' : 'middle';
      const left = anchor === 'end' ? px - w : px - w / 2;
      const showLabel = left > lastRight + 6;
      if (showLabel) lastRight = left + w;
      return { ...m, showLabel, anchor };
    });
}

function ExtremeLabels({
  mode,
  points,
  x,
  y,
  color,
  valueFormat,
  showTimes,
  bounds,
  curveY,
  now,
  nowLine,
}: {
  mode: 'window' | 'local';
  points: SeriesPoint[];
  x: (t: number) => number;
  y: (v: number) => number;
  color: string;
  valueFormat: (v: number) => string;
  showTimes: boolean;
  bounds: Box;
  curveY: (x: number) => number | null;
  now: Circle | null;
  /** The now cursor's hairline, which a label must not straddle either. */
  nowLine: Box | null;
}) {
  let marks: Array<{ point: SeriesPoint & { v: number }; kind: 'max' | 'min' }>;
  if (mode === 'local') {
    marks = localExtremes(points);
    if (marks.length === 0) {
      mode = 'window'; // too flat/short for turns — fall back to the window pair
    }
  }
  if (mode === 'window') {
    const ext = findExtremes(points);
    if (ext === null) return null;
    const flat = ext.max.t === ext.min.t && ext.max.v === ext.min.v;
    marks = flat
      ? [{ point: ext.max, kind: 'max' }]
      : [
          { point: ext.max, kind: 'max' },
          { point: ext.min, kind: 'min' },
        ];
  }
  // Each label keeps clear of every mark (its ring included), the now dot,
  // the curve and the labels placed before it, or the mark goes unlabelled
  // (TOM-445). Widths are estimates, so they carry a wide font's slack.
  const circles: Circle[] = [
    ...marks!.map(({ point: p }) => ({ cx: x(p.t), cy: y(p.v), r: 4.5 })),
    ...(now !== null ? [now] : []),
  ];
  const taken: Box[] = nowLine !== null ? [nowLine] : [];
  const placed = marks!.map(({ point: p, kind }) => {
    const text = valueFormat(p.v);
    const time = showTimes ? formatClock(p.t) : null;
    const place = placeExtremeLabel({
      kind,
      mark: { x: x(p.t), y: y(p.v) },
      value: { width: estimateTextWidth(text, 11) * LABEL_WIDTH_SLACK, sizePx: 11 },
      time:
        time === null ? null : { width: estimateTextWidth(time, 9) * LABEL_WIDTH_SLACK, sizePx: 9 },
      bounds,
      curveY,
      circles,
      taken,
    });
    if (place !== null) taken.push(...place.boxes);
    return { p, kind, text, time, place };
  });
  return (
    <g>
      {placed.map(({ p, kind, text, time, place }) => {
        const spot = place?.spot ?? null;
        // On one row the time follows the value in the same text run.
        const oneRow = spot !== null && spot.value.anchor !== 'middle';
        return (
          <g key={`${kind}-${p.t}`}>
            <circle
              cx={x(p.t)}
              cy={y(p.v)}
              r={3.5}
              fill={color}
              stroke="var(--chart-surface)"
              strokeWidth={2}
            />
            {spot !== null && (
              <text
                className="chart-mark-label"
                x={spot.value.x}
                y={spot.value.y}
                textAnchor={spot.value.anchor}
              >
                {text}
                {oneRow && spot.time !== null && time !== null && (
                  <tspan className="chart-mark-time" dx={4}>
                    {time}
                  </tspan>
                )}
              </text>
            )}
            {spot !== null && !oneRow && spot.time !== null && time !== null && (
              <text
                className="chart-mark-time"
                x={spot.time.x}
                y={spot.time.y}
                textAnchor={spot.time.anchor}
              >
                {time}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}

/** Text widths are estimates (0.58 em a character): room for a wider font. */
const LABEL_WIDTH_SLACK = 1.2;

/** Thin columns on their own zero-based scale, hugging the plot's bottom. */
function BarsStrip({
  bars,
  x,
  stripTop,
  stripBottom,
  plotLeft,
  plotW,
}: {
  bars: HistoryBars;
  x: (t: number) => number;
  stripTop: number;
  stripBottom: number;
  plotLeft: number;
  plotW: number;
}) {
  const vals = bars.points.filter(
    (p): p is SeriesPoint & { v: number } => p.v !== null && Number.isFinite(p.v)
  );
  if (vals.length === 0) return null;
  const maxV = Math.max(...vals.map((p) => p.v), 0);
  if (maxV <= 0) return null;
  const color = toneColor(bars.tone ?? BAR_STRIP_TONE);
  const stripH = stripBottom - stripTop;
  const spacing = plotW / Math.max(1, vals.length);
  const barW = Math.max(1.5, Math.min(6, spacing * 0.72));
  return (
    <g>
      {/* hairline separating the strip from the line panel */}
      <line
        x1={plotLeft}
        x2={plotLeft + plotW}
        y1={stripTop - 4}
        y2={stripTop - 4}
        stroke="var(--chart-grid)"
        strokeWidth={0.5}
      />
      {vals.map((p) => {
        const h = (p.v / maxV) * (stripH - 2);
        if (h < 0.5) return null;
        const bx = x(p.t) - barW / 2;
        const by = stripBottom - h;
        const r = Math.min(2, barW / 2, h);
        return (
          <path
            key={p.t}
            d={`M${bx},${stripBottom}L${bx},${by + r}Q${bx},${by} ${bx + r},${by}L${bx + barW - r},${by}Q${bx + barW},${by} ${bx + barW},${by + r}L${bx + barW},${stripBottom}Z`}
            fill={color}
            opacity={0.75}
          />
        );
      })}
    </g>
  );
}
